<template>
  <div class="support-channels" :class="`support-channels--${variant}`">
    <template v-for="channel in order" :key="channel">
      <button v-if="channel === 'chat'" type="button" class="support-channel" @click="openChat">
        <UiIcon :icon="OnlineChatIcon" :size="32"/>
        {{ t('online_chat') }}
      </button>
      <a v-else-if="channel === 'telegram'" :href="TELEGRAM_URL" target="_blank" class="support-channel">
        <UiIcon :icon="TelegramIcon" :size="32"/>
        Telegram
      </a>
    </template>
  </div>
</template>

<script setup lang="ts">
import {UiIcon} from '@ismet/vue-ui-kit';

import {useI18n} from '@/shared/lib/i18n';
import {useSupportChatStore} from '@/shared/model/supportChat.store';

import OnlineChatIcon from '@/shared/ui/icons/social/OnlineChat.vue';
import TelegramIcon from '@/shared/ui/icons/social/Telegram.vue';

import type {SupportChannel} from '../model/types';

interface IProps {
  /** Порядок кнопок зависит от места вызова */
  order?: SupportChannel[];
  /** filled — серые плитки, plain — без фона */
  variant?: 'filled' | 'plain';
}

interface IEmits {
  /** Чат уже открыт — родитель закрывает своё окно */
  (e: 'chat'): void;
}

withDefaults(defineProps<IProps>(), {
  order: () => ['chat', 'telegram'],
  variant: 'filled',
});
const emits = defineEmits<IEmits>();

const TELEGRAM_URL = 'https://t.me/TelecomKZBot';

const {t} = useI18n();
const supportChatStore = useSupportChatStore();

const openChat = () => {
  supportChatStore.openChat();
  emits('chat');
};
</script>

<style scoped>
.support-channels {
  display: flex;
  gap: 0.25rem;
}

.support-channel {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 0.75rem 0.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--BW-800);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}

.support-channels--filled .support-channel {
  background: var(--BW-100);

  &:hover {
    background: var(--BW-200);
  }
}

/* Без фона растянутые кнопки не читаются — держим их по содержимому */
.support-channels--plain .support-channel {
  flex: none;

  &:hover {
    color: var(--BW-blue-4);
  }
}
</style>

<i18n lang="json">
{
  "ru": {
    "online_chat": "Онлайн чат"
  },
  "kk": {
    "online_chat": "Онлайн чат"
  },
  "en": {
    "online_chat": "Online chat"
  }
}
</i18n>
